// Generated by TaroBase SDK Generator: 2026-04-14T11:42:05.667Z
// Collection: artistBio/$bioId
// Edits may be overwritten by subsequent runs!

import {
  get, getMany, set, subscribe, getFiles, setFile, runQuery, count, aggregate, transformValues,
  Time, Token, Increment, Address, PublicKey,
  TimeOperation, IncrementOperation, TokenAmount, AddressType,
  TarobaseMetadata, DocumentOperation, FileItem, setMany, formatError,
} from '../db-client';
import type { AggregateResult, AggregateOperation } from '../db-client';

/**
 * Single document storing the artist bio info for the Artist Room feature. Admin and founder write only, public read.
 */

export interface ArtistBioRequest {
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  videoUrl?: string;
  xAccount?: string;
}

export interface ArtistBioResponse {
  name: string;
  role: string;
  bio: string;
  imageUrl?: string;
  videoUrl?: string;
  xAccount?: string;
  id: string;
  tarobase_created_at: number;
}

/**
 * Build a ArtistBio operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildArtistBio(bioId: string, data?: ArtistBioRequest): DocumentOperation {
  const doc = data ? transformValues(data) : {};
  return { path: `artistBio/${bioId}`, document: doc };
}

/**
 * Admin or founder only. Creates the artist bio document with name, role, bio text, optional imageUrl, videoUrl, and xAccount (X/Twitter username or profile URL). Name, role, and bio are immutable after creation. (Create/Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the write worked.
 */
export async function setArtistBio(bioId: string, data?: ArtistBioRequest): Promise<boolean> {
  try {
    const transformedData = transformValues(data || {});
    const succeeded = await set(`artistBio/${bioId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error setting ArtistBio: ${formatError(error)}`);
    return false;
  }
}

export type ArtistBioRequestUpdate = Partial<ArtistBioRequest>;

/**
 * Build a ArtistBio update operation for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildUpdateArtistBio(bioId: string, data: ArtistBioRequestUpdate): DocumentOperation {
  const doc = transformValues(data);
  return { path: `artistBio/${bioId}`, document: doc };
}

/**
 * Admin or founder only. Can update imageUrl, videoUrl, and xAccount. Name, role, and bio cannot be changed after creation. (Update Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the update worked.
 */
export async function updateArtistBio(bioId: string, data: ArtistBioRequestUpdate): Promise<boolean> {
  try {
    const transformedData = transformValues(data);
    const succeeded = await set(`artistBio/${bioId}`, transformedData);
    return !!succeeded;
  } catch (error) {
    console.error(`Error updating ArtistBio: ${formatError(error)}`);
    return false;
  }
}

/**
 * 
  Read Operation Details: Anyone can read the artist bio. Displayed publicly on the Artist Room page.
   (Get Single Item)
 */
export async function getArtistBio(bioId: string): Promise<ArtistBioResponse | null> {
  try {
    const result = await get(`artistBio/${bioId}`);
    return result as ArtistBioResponse | null;
  } catch (error) {
    console.error(`Error getting ArtistBio: ${formatError(error)}`);
    return null;
  }
}

/**
 * Subscribes to changes in a single ArtistBio document. (
  Read Operation Details: Anyone can read the artist bio. Displayed publicly on the Artist Room page.
  )
 */
export function subscribeArtistBio(callback: (data: ArtistBioResponse | null) => void, bioId: string): Promise<() => Promise<void>> {
  return subscribe(`artistBio/${bioId}`, { // Use the item's basePath
    onData: (data) => {
      callback(data as ArtistBioResponse | null);
    },
    onError: (error) => {
      console.error(`Error subscribing to ArtistBio at path '${`artistBio/${bioId}`}': ${formatError(error)}`);
    }
  });
}

/**
 * Get many ArtistBio items from collection artistBio
 
  Read Operation Details: Anyone can read the artist bio. Displayed publicly on the Artist Room page.
  
 */
export async function getManyArtistBio(filter: string = ""): Promise<ArtistBioResponse[]> {
  try {
    const results = await get("artistBio", { prompt: filter });
    return Array.isArray(results) ? results as ArtistBioResponse[] : [];
  } catch (error) {
    console.error(`Error getting ArtistBio collection at path 'artistBio': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in ArtistBio collection at artistBio
 
  Read Operation Details: Anyone can read the artist bio. Displayed publicly on the Artist Room page.
  
 */
export function subscribeManyArtistBio(callback: (data: ArtistBioResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("artistBio", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as ArtistBioResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to ArtistBio collection at path '${"artistBio"}': ${formatError(error)}`);
    }
  });
}

/**
 * Get all ArtistBio items from collection artistBio
 
  Read Operation Details: Anyone can read the artist bio. Displayed publicly on the Artist Room page.
  
 */
export async function getAllArtistBio(filter: string = ""): Promise<ArtistBioResponse[]> {
  try {
    const results = await get("artistBio", { prompt: filter });
    return Array.isArray(results) ? results as ArtistBioResponse[] : [];
  } catch (error) {
    console.error(`Error getting ArtistBio collection at path 'artistBio': ${formatError(error)}`);
    return [];
  }
}

/**
 * Subscribe to changes in ArtistBio collection at artistBio
 
  Read Operation Details: Anyone can read the artist bio. Displayed publicly on the Artist Room page.
  
 */
export function subscribeAllArtistBio(callback: (data: ArtistBioResponse[]) => void, filter: string = ""): Promise<() => Promise<void>> {
  return subscribe("artistBio", {
    prompt: filter,
    onData: (data) => {
      callback(Array.isArray(data) ? data as ArtistBioResponse[] : []);
    },
    onError: (error) => {
       console.error(`Error subscribing to ArtistBio collection at path '${"artistBio"}': ${formatError(error)}`);
    }
  });
}

/**
 * Count ArtistBio items in collection artistBio.
 * Returns a numeric count instead of fetching full documents — much more efficient.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param filter - Optional natural language filter (e.g., "created in the last 7 days")
 * @returns AggregateResult with the count value
 */
export async function countArtistBio(filter: string = ""): Promise<AggregateResult> {
  try {
    return await count("artistBio", { prompt: filter || undefined });
  } catch (error) {
    console.error(`Error counting ArtistBio at path 'artistBio': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * Run an aggregate operation on ArtistBio items in collection artistBio.
 * Returns a single numeric value instead of fetching full documents.
 *
 * Supported operations: 'count', 'uniqueCount', 'sum', 'avg', 'min', 'max'.
 * For 'sum', 'avg', 'min', 'max', and 'uniqueCount', you must provide the field name.
 *
 * NOTE: This only works because the read policy for this collection is "true".
 * @param operation - The aggregate operation to perform
 * @param opts - Options including optional filter prompt and field name
 * @returns AggregateResult with the computed numeric value
 */
export async function aggregateArtistBio(operation: AggregateOperation, opts?: { filter?: string; field?: string }): Promise<AggregateResult> {
  try {
    return await aggregate("artistBio", operation, { prompt: opts?.filter || undefined, field: opts?.field });
  } catch (error) {
    console.error(`Error aggregating ArtistBio at path 'artistBio': ${formatError(error)}`);
    return { value: 0 };
  }
}

/**
 * 
  Delete Operation Details: Admin or founder only. Removes the artist bio document.
   (Delete Single Item)
 * @returns A boolean indicating whether the operation succeeded (true) or failed (false). Always check this value to confirm the delete worked.
 */
export async function deleteArtistBio(bioId: string): Promise<boolean> {
  try {
    const succeeded = await set(`artistBio/${bioId}`, null);
    return !!succeeded;
  } catch (error) {
    console.error(`Error deleting ArtistBio: ${formatError(error)}`);
    return false;
  }
}

/**
 * Build a delete operation for ArtistBio for use with setMany.
 * @returns A DocumentOperation that can be passed to setMany.
 */
export function buildDeleteArtistBio(bioId: string): DocumentOperation {
  return { path: `artistBio/${bioId}`, document: null };
}

